// File: frontend/src/services/entryService.ts
import { EntryModel } from "../models";
import { ENTRIES_PER_PAGE , API_BASE_URL } from "../constants";
import i18n from '../i18n';
import { parseServerDate } from "../utils/dateUtils";
import { mapRawEntry } from "./entryMapper";

// Corrected EntryResponse to match your API's flat structure
export interface EntryResponse {
  entries: EntryModel[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
}

export const fetchEntriesByMatchId = async (
  matchId: number,
  page: number = 1,
  pageSize: number = ENTRIES_PER_PAGE,
  showRookieEntries: boolean = true,
  sort: string = "oldest",
  filterToday: boolean = false,
  rookieOnly: boolean = false,
  keyword: string = ""
): Promise<EntryResponse> => {
  // Replace hardcoded string for "Match ID is required"
  if (!matchId) throw new Error(i18n.t('error_match_id_required')); // New translation key needed

  const baseUrl = `${API_BASE_URL}/api/entries/matchId/${matchId}`;
  // Build query params — keyword is optional and omitted when empty
  const params = new URLSearchParams({
    page: String(page - 1),
    size: String(pageSize),
    showRookieEntries: String(showRookieEntries),
    sort,
    filterToday: String(filterToday),
    rookieOnly: String(rookieOnly),
  });
  if (keyword && keyword.trim()) {
    params.append("keyword", keyword.trim());
  }
  const url = `${baseUrl}?${params.toString()}`;
  
  // BUG FIX: Send token so backend can determine currentUserRating and currentUserHasFav
  const token = localStorage.getItem("token");
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const response = await fetch(url, { headers });

  if (!response.ok) {
    throw new Error(i18n.t('error_fetch_entries_by_match_id_failed'));
  }

  const responseJson = await response.json();

  // Calculate total pages on client side for consistency
  const calculatedTotalPages = Math.ceil(responseJson.totalItems / pageSize);

  // The API response is flat, so we directly return its properties
  return {
    entries: responseJson.content.map(mapRawEntry),
    // Use calculated values instead of server-provided ones for pagination
    currentPage: page, // Keep the original page number (1-based)
    totalPages: calculatedTotalPages, // Use client-calculated total pages
    totalItems: responseJson.totalItems,
  };
};

/**
 * Fetches a single entry by its id.
 *
 * Used by the (#id) reference links: the id alone does not say which of the
 * five topic types the entry belongs to, so the entry has to be fetched to
 * build the deep link to its page.
 */
export const fetchEntryById = async (entryId: number): Promise<EntryModel> => {
  const token = localStorage.getItem("token");
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}/api/entries/entry/${entryId}`, { headers });

  if (!response.ok) {
    throw new Error(i18n.t("error_fetch_entry_failed"));
  }

  return mapRawEntry(await response.json());
};

// PUT /api/entries/{id} — update own entry content
export const updateEntry = async (
  entryId: number,
  content: string
): Promise<EntryModel> => {
  const token = localStorage.getItem("token");
  const response = await fetch(
    `${API_BASE_URL}/api/entries/${entryId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ content }),
    }
  );

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || i18n.t("entry_update_failed"));
  }

  const entry = await response.json();
  return {
    id: entry.id,
    content: entry.content,
    createdAt: parseServerDate(entry.createdAt),
    editedAt: parseServerDate(entry.editedAt),
    userId: entry.userId,
    username: entry.username,
    authorRole: entry.authorRole || "ROOKIE",
    matchId: entry.matchId,
    homeTeam: entry.homeTeam || "",
    awayTeam: entry.awayTeam || "",
    matchDate: entry.matchDate ? new Date(entry.matchDate) : new Date(),
    sportType: entry.sportType,
    plusCount: entry.plusCount || 0,
    minusCount: entry.minusCount || 0,
    favCount: entry.favCount || 0,
    currentUserRating: entry.currentUserRating as any,
    currentUserHasFav: entry.currentUserHasFav || false,
    personId:          entry.personId ?? null,
    personFullName:    entry.personFullName ?? null,
    personSlug:        entry.personSlug ?? null,
    clubId:            entry.clubId ?? null,
    clubName:          entry.clubName ?? null,
    clubSlug:          entry.clubSlug ?? null,
    teamId:            entry.teamId ?? null,
    teamName:          entry.teamName ?? null,
    teamSlug:          entry.teamSlug ?? null,
  };
};

/**
 * PATCH /api/entries/{id}/remove-image — unpublish one image from an own entry.
 *
 * Used by the "yayından kaldır" action of the profile album. imageRef is the
 * raw reference as written inside the (gorsel: ...) token (opaque id or legacy
 * URL), not the resolved src. The backend removes every token with that
 * reference and returns the updated entry; the edit window does not apply.
 *
 * Throws with the backend error code as message (e.g. ENTRY_IMAGE_NOT_FOUND),
 * so callers can translate it with parseApiError.
 */
export const removeEntryImage = async (
  entryId: number,
  imageRef: string
): Promise<EntryModel> => {
  const token = localStorage.getItem("token");
  const response = await fetch(
    `${API_BASE_URL}/api/entries/${entryId}/remove-image`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ imageRef }),
    }
  );

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || i18n.t("lightbox_unpublish_error"));
  }

  return mapRawEntry(await response.json());
};

// Issue B: DELETE /api/entries/{id} — soft-delete own entry
export const deleteEntry = async (entryId: number): Promise<void> => {
  const token = localStorage.getItem("token");
  const response = await fetch(
    `${API_BASE_URL}/api/entries/${entryId}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  );

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || i18n.t("entry_delete_error"));
  }
};

/**
 * Lightweight endpoint to get the total entry count across all matches in a date range.
 * Used for polling to detect new entries without fetching full data.
 * Sends the auth token when available so the backend excludes the current user's
 * own entries — preventing the badge from counting entries they just wrote themselves.
 *
 * The window is sent as two UTC instants ("YYYY-MM-DDTHH:mm:ss"), the same pair the
 * left pane sends to /api/matches/filter. A match belongs to the day it is played in
 * the VIEWER's timezone, so the badge and the pane must use the identical window —
 * with plain calendar dates the two disagreed about matches near the day boundary.
 */
export const fetchTotalEntryCount = async (
  startDateTime: string,
  endDateTime: string,
  showRookieEntries: boolean = true
): Promise<number> => {
  const baseUrl = `${API_BASE_URL}/api/entries/count/total`;
  const url = `${baseUrl}?startDateTime=${encodeURIComponent(startDateTime)}&endDateTime=${encodeURIComponent(endDateTime)}&showRookieEntries=${showRookieEntries}`;
  const token = localStorage.getItem("token");
  const response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) return -1;
  return response.json();
};

// Issue B: PATCH /api/entries/{id}/restore — restore own self-deleted entry
export const restoreEntry = async (entryId: number): Promise<EntryModel> => {
  const token = localStorage.getItem("token");
  const response = await fetch(
    `${API_BASE_URL}/api/entries/${entryId}/restore`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  );

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || i18n.t("entry_restore_error"));
  }

  const entry = await response.json();
  return {
    id: entry.id,
    content: entry.content,
    createdAt: parseServerDate(entry.createdAt),
    editedAt: parseServerDate(entry.editedAt),
    userId: entry.userId,
    username: entry.username,
    authorRole: entry.authorRole || "ROOKIE",
    matchId: entry.matchId,
    homeTeam: entry.homeTeam || "",
    awayTeam: entry.awayTeam || "",
    matchDate: entry.matchDate ? new Date(entry.matchDate) : new Date(),
    sportType: entry.sportType,
    plusCount: entry.plusCount || 0,
    minusCount: entry.minusCount || 0,
    favCount: entry.favCount || 0,
    currentUserRating: entry.currentUserRating as any,
    currentUserHasFav: entry.currentUserHasFav || false,
    personId:          entry.personId ?? null,
    personFullName:    entry.personFullName ?? null,
    personSlug:        entry.personSlug ?? null,
    clubId:            entry.clubId ?? null,
    clubName:          entry.clubName ?? null,
    clubSlug:          entry.clubSlug ?? null,
    teamId:            entry.teamId ?? null,
    teamName:          entry.teamName ?? null,
    teamSlug:          entry.teamSlug ?? null,
  };
};